import clsx from 'clsx';
import {
  Activity,
  AlertCircle,
  Bot,
  Check,
  ChevronRight,
  Loader2,
  Minus,
  Sparkles,
} from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { AgentActivityEvent } from '../useChatStore';
import styles from './AgentActivity.module.scss';
import type {
  ActivityNode,
  ActivityStatus,
  AgentActivityModel,
  ToolNode,
} from './agentActivityModel';
import { buildAgentActivityModel } from './agentActivityModel';

interface AgentActivityProps {
  events: AgentActivityEvent[];
  /** True while this message's turn is still streaming (live affordances). */
  isStreaming: boolean;
}

const statusClass: Record<ActivityStatus, string> = {
  success: styles.statusSuccess,
  running: styles.statusRunning,
  error: styles.statusError,
  skipped: styles.statusSkipped,
};

/** Pretty-print a tool input/result for a panel; null when there is nothing. */
const formatValue = (value: unknown): string | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
};

const summaryText = (summary: AgentActivityModel['summary']): string => {
  const parts: string[] = [];
  if (summary.tools > 0) {
    parts.push(`${summary.tools} tool${summary.tools === 1 ? '' : 's'}`);
  }
  if (summary.subagents > 0) {
    parts.push(
      `${summary.subagents} subagent${summary.subagents === 1 ? '' : 's'}`
    );
  }
  if (summary.thoughts > 0) {
    parts.push(
      `${summary.thoughts} thought${summary.thoughts === 1 ? '' : 's'}`
    );
  }

  return parts.length > 0 ? `Used ${parts.join(' · ')}` : 'Agent activity';
};

const formatDuration = (ms: number): string =>
  ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;

/** The status disc (28px, or 22px nested) with the right icon + colour. */
const StatusNode: FC<{
  status: ActivityStatus;
  isThinking?: boolean;
  isSubagent?: boolean;
  isSmall?: boolean;
  isAnimating?: boolean;
}> = ({
  status,
  isThinking = false,
  isSubagent = false,
  isSmall = false,
  isAnimating = false,
}) => {
  const size = isSmall ? 13 : 15;
  // Running / error / skipped take precedence over the subagent icon so an
  // in-flight or failed subagent shows its real state (spinner / alert) instead
  // of a "done" teal bot; the teal bot is reserved for a completed subagent.
  const icon: ReactNode = isThinking ? (
    <Sparkles size={size} aria-hidden={true} />
  ) : status === 'running' ? (
    <Loader2
      size={size}
      aria-hidden={true}
      className={isAnimating ? styles.spinner : undefined}
    />
  ) : status === 'error' ? (
    <AlertCircle size={size} aria-hidden={true} />
  ) : status === 'skipped' ? (
    <Minus size={size} aria-hidden={true} />
  ) : isSubagent ? (
    <Bot size={size} aria-hidden={true} />
  ) : (
    <Check size={size} aria-hidden={true} />
  );

  return (
    <span
      className={clsx(
        styles.node,
        isSmall && styles.nodeSmall,
        isThinking
          ? styles.statusThinking
          : isSubagent && status === 'success'
            ? styles.statusSubagent
            : statusClass[status]
      )}
    >
      {icon}
    </span>
  );
};

/** Input/Result mono panel; Result scrolls at 200px. */
const Panel: FC<{ label: string; body: string; result?: boolean }> = ({
  label,
  body,
  result = false,
}) => (
  <div className={styles.panel}>
    <span className={styles.panelLabel}>{label}</span>
    <pre className={clsx(styles.panelBody, result && styles.panelResult)}>
      {body}
    </pre>
  </div>
);

const statusTextFor = (node: ToolNode): ReactNode => {
  if (node.status === 'error') {
    return <span className={styles.statusTextError}>{'failed'}</span>;
  }
  if (node.status === 'skipped') {
    return (
      <span className={styles.statusTextSkipped}>
        {node.skippedLabel ?? 'skipped'}
      </span>
    );
  }

  return null;
};

interface RowProps {
  node: ActivityNode;
  isOpen: (id: string) => boolean;
  toggle: (id: string) => void;
  isStreaming: boolean;
  small?: boolean;
  last?: boolean;
}

/** One timeline row + its expanded body; recurses for groups / subagents. */
const Row: FC<RowProps> = ({
  node,
  isOpen,
  toggle,
  isStreaming,
  small = false,
  last = false,
}) => {
  const open = isOpen(node.id);

  const thinking = node.kind === 'thinking';
  const subagent = node.kind === 'subagent';
  const animate = isStreaming && node.status === 'running';

  const body = renderBody(node, { isOpen, toggle, isStreaming });
  const expandable = body !== null;

  const label =
    node.kind === 'thinking'
      ? node.redacted
        ? 'Thinking'
        : 'Thought'
      : node.label;

  return (
    <div className={styles.row}>
      <div className={styles.rail}>
        <StatusNode
          status={node.status}
          isThinking={thinking}
          isSubagent={subagent}
          isSmall={small}
          isAnimating={animate}
        />
        {!last && <span className={styles.connector} />}
      </div>
      <div className={styles.content}>
        <button
          type={'button'}
          className={clsx(
            styles.stepHeader,
            !expandable && styles.stepHeaderStatic
          )}
          onClick={expandable ? () => toggle(node.id) : undefined}
          aria-expanded={expandable ? open : undefined}
          disabled={!expandable}
          data-testid={'agent-activity-step'}
        >
          {expandable && (
            <ChevronRight
              size={14}
              aria-hidden={true}
              className={clsx(styles.stepChevron, open && styles.chevronOpen)}
            />
          )}
          <span className={styles.stepLabel} title={label}>
            {label}
          </span>
          {node.kind === 'tool-group' && (
            <span className={styles.countPill}>{`×${node.count}`}</span>
          )}
          {node.kind === 'tool' && statusTextFor(node)}
          {node.kind === 'tool' && node.durationMs !== undefined && (
            <span className={styles.duration}>
              {formatDuration(node.durationMs)}
            </span>
          )}
        </button>
        {expandable && open && body}
      </div>
    </div>
  );
};

interface RenderCtx {
  isOpen: (id: string) => boolean;
  toggle: (id: string) => void;
  isStreaming: boolean;
}

/** Expanded body for a node, or null when the node has nothing to drill into. */
const renderBody = (node: ActivityNode, ctx: RenderCtx): ReactNode => {
  if (node.kind === 'thinking') {
    if (node.redacted) {
      return (
        <div className={styles.stepBody}>
          <span className={styles.reasoningHidden}>
            {'Reasoning hidden for this step.'}
          </span>
        </div>
      );
    }

    return (
      <div className={styles.stepBody}>
        <p className={styles.reasoning}>{node.text}</p>
      </div>
    );
  }

  if (node.kind === 'tool') {
    const input = formatValue(node.input);
    const result = formatValue(node.result);
    if (input === null && result === null) {
      return null;
    }

    return (
      <div className={styles.stepBody}>
        {input !== null && <Panel label={'Input'} body={input} />}
        {result !== null && (
          <div>
            <Panel label={'Result'} body={result} result={true} />
            {node.resultTruncated && (
              <p className={styles.truncatedNote}>
                {
                  'Result truncated — the tool returned more than is shown here.'
                }
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  if (node.kind === 'tool-group') {
    return (
      <div className={styles.subTimeline}>
        {node.calls.map((call, index) => (
          <Row
            key={call.id}
            node={{ ...call, label: `Call ${index + 1}` }}
            isOpen={ctx.isOpen}
            toggle={ctx.toggle}
            isStreaming={ctx.isStreaming}
            small={true}
            last={index === node.calls.length - 1}
          />
        ))}
      </div>
    );
  }

  if (node.kind === 'subagent') {
    return (
      <div className={styles.stepBody}>
        {node.taskDescription && (
          <p className={styles.reasoning}>{node.taskDescription}</p>
        )}
        <div className={styles.subTimeline}>
          {node.steps.map((step, index) => (
            <Row
              key={step.id}
              node={step}
              isOpen={ctx.isOpen}
              toggle={ctx.toggle}
              isStreaming={ctx.isStreaming}
              small={true}
              last={index === node.steps.length - 1}
            />
          ))}
        </div>
      </div>
    );
  }

  return null;
};

/**
 * The "Agent activity" block rendered beneath a read-only Agent message
 * (AID-361). Collapsed by default to a quiet grey footnote; expands to a white
 * card with a status-coloured timeline. Reads a pure view-model derived from
 * the streamed activity log — no hand-authored data, no SDK coupling.
 */
export const AgentActivity: FC<AgentActivityProps> = ({
  events,
  isStreaming,
}) => {
  const model = useMemo(
    () => buildAgentActivityModel(events, isStreaming),
    [events, isStreaming]
  );

  const [expanded, setExpanded] = useState(false);
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(
    () => new Set<string>()
  );

  const isOpen = useCallback((id: string) => openIds.has(id), [openIds]);
  const toggle = useCallback((id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  if (model.nodes.length === 0 && !model.isRunning) {
    return null;
  }

  const summary = summaryText(model.summary);

  if (!expanded) {
    return (
      <div className={styles.activity} data-testid={'agent-activity'}>
        <button
          type={'button'}
          className={styles.collapsedTrigger}
          onClick={() => setExpanded(true)}
          aria-expanded={false}
          data-testid={'agent-activity-toggle'}
        >
          <ChevronRight
            size={14}
            aria-hidden={true}
            className={styles.collapsedChevron}
          />
          {summary}
        </button>
        {model.isRunning && (
          <div
            className={styles.runningLine}
            data-testid={'agent-activity-running'}
          >
            <Loader2 size={12} aria-hidden={true} className={styles.spinner} />
            {model.runningLabel ?? 'Working…'}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.activity} data-testid={'agent-activity'}>
      <div className={styles.card}>
        <button
          type={'button'}
          className={styles.header}
          onClick={() => setExpanded(false)}
          aria-expanded={true}
          data-testid={'agent-activity-toggle'}
        >
          <Activity
            size={16}
            aria-hidden={true}
            className={styles.headerIcon}
          />
          <span className={styles.headerTitle}>{'Agent activity'}</span>
          <span className={styles.headerSummary}>{summary}</span>
          <ChevronRight
            size={16}
            aria-hidden={true}
            className={clsx(styles.headerChevron, styles.chevronOpen)}
          />
        </button>
        <div className={styles.timeline}>
          {model.nodes.map((node, index) => (
            <Row
              key={node.id}
              node={node}
              isOpen={isOpen}
              toggle={toggle}
              isStreaming={isStreaming}
              last={index === model.nodes.length - 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
